import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

/* eslint-disable sonarjs/cognitive-complexity */

/* eslint-disable consistent-return */
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Custom type guard
function isKeyboardEvent(event) {
  return event.key !== undefined;
} // Hook to manage accessibility logic of menu
// Needs to now number of menu items in advance


function useMenu(numberOfItems) {
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var isMounted = useRef(null);
  var buttonRef = useRef(null);
  var currentFocusIndex = useRef(null); // Track whether menu got opened via click but not a keyboard event

  var clickOpened = useRef(false);
  var itemsRefs = useMemo(function () {
    return _toConsumableArray(Array(numberOfItems).keys()).map(function () {
      return /*#__PURE__*/createRef();
    });
  }, [numberOfItems]); // Handle focus moving between items

  var moveFocus = useCallback(function (itemIndex) {
    var _itemsRefs$itemIndex$;

    currentFocusIndex.current = itemIndex;
    (_itemsRefs$itemIndex$ = itemsRefs[itemIndex].current) === null || _itemsRefs$itemIndex$ === void 0 ? void 0 : _itemsRefs$itemIndex$.focus();
  }, [itemsRefs]); // Simple check whether target component is actually mounted

  useEffect(function () {
    isMounted.current = true;
    return function () {
      isMounted.current = false;
    };
  }, []); // If the menu is open focus on the first item
  // But only if it was opened by keyboard event

  useEffect(function () {
    if (isOpen && !clickOpened.current) {
      moveFocus(0);
    } else if (!isOpen) {
      clickOpened.current = false;
    }
  }, [moveFocus, isOpen]); // Disable scrolling with arrows when the menu is opened

  useEffect(function () {
    function disableArrowScroll(event) {
      if (isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
      }
    }

    document.addEventListener('keydown', disableArrowScroll);
    return function () {
      return document.removeEventListener('keydown', disableArrowScroll);
    };
  }, [isOpen]); // Handle clicks and closing menu automatically

  useEffect(function () {
    // Ignore if the menu is closed
    if (!isOpen) {
      return;
    }

    function handleEveryClick(event) {
      setTimeout(function () {
        // Make sure we are targetting valid element
        if (!(event.target instanceof Element)) {
          return;
        }

        if (isMounted.current) {
          setIsOpen(false);
        }
      }, 10);
    }

    document.addEventListener('click', handleEveryClick);
    return function () {
      document.removeEventListener('click', handleEveryClick);
    };
  }, [isOpen]); // Handle menu button clicks and keyboard events

  function buttonListener(event) {
    // Detect if event was a keyboard event or a mouse event
    if (isKeyboardEvent(event)) {
      var key = event.key;

      if (!['Enter', ' ', 'Tab', 'ArrowDown'].includes(key)) {
        return;
      }

      if ((key === 'Tab' || key === 'ArrowDown') && clickOpened.current && isOpen) {
        event.preventDefault();
        moveFocus(0);
      } else if (key !== 'Tab') {
        event.preventDefault();
        setIsOpen(true);
      }
    } else {
      clickOpened.current = !isOpen;
      setIsOpen(!isOpen);
    }
  } // Handle menu items clicks and keyboard events


  function itemListener(event) {
    var key = event.key; // Handle keyboard controls

    if (['Tab', 'Shift', 'Enter', 'Escape', 'ArrowUp', 'ArrowDown', ' '].includes(key)) {
      // Create mutable value that initializes as the currentFocusIndex value
      var newFocusIndex = currentFocusIndex.current; // Controls whether the menu is open or closed
      // If the button should regain focus on close
      // If a handler function should be called

      if (key === 'Escape') {
        var _buttonRef$current;

        setIsOpen(false);
        (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.focus();
        return;
      }

      if (key === 'Tab') {
        setIsOpen(false);
        return;
      }

      if (key === 'Enter' || key === ' ') {
        event.currentTarget.click();
        setIsOpen(false);
        return;
      } // Controls the current index to focus


      if (newFocusIndex !== null) {
        if (key === 'ArrowUp') {
          newFocusIndex -= 1;
        } else if (key === 'ArrowDown') {
          newFocusIndex += 1;
        }

        if (newFocusIndex > itemsRefs.length - 1) {
          newFocusIndex = 0;
        } else if (newFocusIndex < 0) {
          newFocusIndex = itemsRefs.length - 1;
        }
      } // After any modification set state to the modified value


      if (newFocusIndex !== null) {
        moveFocus(newFocusIndex);
      }
    }
  }

  var buttonProps = {
    'aria-expanded': isOpen,
    'aria-haspopup': true,
    onClick: buttonListener,
    onKeyDown: buttonListener,
    ref: buttonRef,
    role: 'button',
    tabIndex: 0
  };

  var itemsProps = _toConsumableArray(Array(numberOfItems).keys()).map(function (index) {
    return {
      onKeyDown: itemListener,
      ref: itemsRefs[index],
      role: 'menuitem',
      tabIndex: -1
    };
  });

  return {
    buttonProps: buttonProps,
    isOpen: isOpen,
    itemsProps: itemsProps
  };
}

export default useMenu;